/* Ronini polish layer (2026-10-10), paired with assets/rn-polish.css. Presentation only:
   1. In-sentence marks: wraps exact words that are already on the page in a span (hand-drawn line, marker,
      weight or colour). The words themselves are never changed; if a sentence is edited later, its mark is
      simply skipped. At most one hand-drawn line per page.
   2. Keeps short phrases and link arrows from breaking across lines; arrows nudge on hover.
   3. Groups of cards below the fold enter with a short stagger.
   4. Flags when the footer is on screen, so the sticky add-to-cart bar and the Rewards button step aside.
   5. Marks the page's one standout line (rn-polish.css section 11).
   6. Drawn babies (snippets/rn-baby.liquid) ink in once below the fold and move only while on screen.
   7. Big numbers count up once; the award band's bottle rises into place. */
(() => {
  if (window.rnPolish) return;
  window.rnPolish = 1;

  const path = location.pathname.replace(/\/+$/, '') || '/';
  const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
  const norm = (s) => (s || '').replace(/\s+/g, ' ').replace(/[\u2018\u2019]/g, "'").trim();
  const main = document.querySelector('main') || document.body;

  /* ---------- 1. In-sentence marks ---------- */
  const on = (re) => re.test(path);
  const MARKS = [
    // Hand-drawn lines: the one word each headline turns on.
    { when: /^\/$|^\/products\/the-ronini-wrapped-glass-bottle$/, text: 'Wrapped, not sprayed.', word: 'sprayed', type: 'strike', sig: true },
    { when: /^\/products\/the-nipple$/, text: 'Choose nipple flow by feeding pace, not age.', word: 'age', type: 'strike' },
    { when: /^\/pages\/about$/, contains: 'trade-off: fragility', word: 'fragility', type: 'underline' },
    { when: /^\/pages\/feeding-101$/, text: "Give a bottle at your baby's pace.", word: "your baby's pace", type: 'underline' },
    { when: /^\/pages\/affiliate$/, text: 'Share Ronini in your own way.', word: 'your own way', type: 'underline' },
    // Periwinkle marker: a defined term, or the most generous line on the page.
    { when: /^\/products\/the-ronini-wrapped-glass-bottle$/, text: "One subscription for baby's first year of bottle feeding.", word: 'first year', type: 'marker' },
    { when: /^\/pages\/about$/, contains: 'called overmolding', word: 'overmolding', type: 'marker' },
    { when: /^\/pages\/materials$/, contains: 'This is called overmolding.', word: 'overmolding', type: 'marker' },
    { when: /^\/policies\/refund-policy$/, contains: 'send the bottles back for a refund', word: 'send the bottles back', type: 'marker' },  // 10-24: the guarantee refund needs the bottles back
    // Weight: the fact inside a sentence.
    { when: /^\/pages\/materials$/, contains: 'It is still glass.', word: 'It is still glass.', type: 'strong' },
    { when: /^\/products\/the-ronini-wrapped-glass-bottle$/, contains: 'Milk contacts glass and silicone.', word: 'glass and silicone', type: 'strong', first: true },
    { when: /^\/products\/the-venting-tube$/, contains: 'No vent to keep facing up', word: 'No vent to keep facing up', type: 'strong', first: true },
    // One orange word: the number that is the offer.
    { when: /^\/pages\/refer-a-friend$/, text: 'Give a friend $10 off.', word: '$10', type: 'spark' },
  ];
  const BLOCKS = 'h1, h2, h3, h4, p, li, dd, span, div, a, strong, b';

  // Deepest elements whose text matches (so a section wrapper that merely contains the sentence is skipped).
  const findBlocks = (m) => {
    const hits = [...main.querySelectorAll(BLOCKS)].filter((el) => {
      const t = norm(el.textContent);
      return m.text ? t === m.text : t.includes(m.contains);
    });
    return hits.filter((el) => !hits.some((other) => other !== el && el.contains(other)));
  };

  const SVG_NS = 'http://www.w3.org/2000/svg';
  const stroke = (type) => {
    const svg = document.createElementNS(SVG_NS, 'svg');
    svg.setAttribute('class', 'rn-mark__svg');
    svg.setAttribute('viewBox', '0 0 100 12');
    svg.setAttribute('preserveAspectRatio', 'none');
    svg.setAttribute('aria-hidden', 'true');
    svg.setAttribute('focusable', 'false');
    const p = document.createElementNS(SVG_NS, 'path');
    p.setAttribute('pathLength', '1');
    // A slightly uneven pen line, not a ruler.
    p.setAttribute('d', type === 'strike'
      ? 'M1 7.2 C 18 5.6, 34 7.8, 52 6.4 S 84 5.2, 99 6.6'
      : 'M1 6 C 16 8.4, 36 4.6, 56 6.8 S 86 8.2, 99 5.4');
    svg.appendChild(p);
    return svg;
  };

  const wrapWord = (block, word, make) => {
    const walker = document.createTreeWalker(block, NodeFilter.SHOW_TEXT);
    const wordRe = new RegExp('(^|[^A-Za-z])(' + word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&').replace(/'/g, "['\u2019]") + ')(?![A-Za-z])');
    let node;
    while ((node = walker.nextNode())) {
      if (node.parentElement.closest('.rn-mark, .rn-hl, .rn-strong, .rn-spark, script, style')) continue;
      const m = wordRe.exec(node.data);
      if (!m) continue;
      const start = m.index + m[1].length;
      const target = node.splitText(start);
      target.splitText(m[2].length);
      const wrap = make();
      target.parentNode.insertBefore(wrap, target);
      wrap.insertBefore(target, wrap.firstChild);
      return wrap;
    }
    return null;
  };

  const marked = [];
  MARKS.filter((m) => on(m.when)).forEach((m) => {
    let blocks = findBlocks(m);
    if (m.first) blocks = blocks.slice(0, 1);
    blocks.forEach((block) => {
      const el = wrapWord(block, m.word, () => {
        const span = document.createElement('span');
        if (m.type === 'strike' || m.type === 'underline') {
          span.className = 'rn-mark rn-mark--' + m.type;
          span.appendChild(stroke(m.type));
        } else {
          span.className = { marker: 'rn-hl', strong: 'rn-strong', spark: 'rn-spark' }[m.type];
        }
        return span;
      });
      if (el && (m.type === 'strike' || m.type === 'underline' || m.type === 'marker')) marked.push(el);
      // The standout line: set large, and its last two words kept together ("not sprayed." never splits).
      if (el && m.sig) {
        block.classList.add('rn-sig');
        const before = el.previousSibling;
        if (before && before.nodeType === 3) before.data = before.data.replace(/ $/, '\u00a0');
      }
    });
  });

  // FAQ answers that open with "Yes." or "No." set that word in 600.
  document.querySelectorAll('.ronini-faqs__answer, .rn-faq__a, .rn-f101p-faq .rn-f101p-acc__part .rn-f101p-rte').forEach((answer) => {
    const first = answer.querySelector('p') || answer;
    wrapWord(first, 'Yes.', () => Object.assign(document.createElement('span'), { className: 'rn-strong' })) ||
      wrapWord(first, 'No.', () => Object.assign(document.createElement('span'), { className: 'rn-strong' }));
  });
  // (Only the first word: a later "No." inside the sentence is left alone.)

  // Draw marks when they come into view (they are drawn already under reduced motion, via CSS). Some sections
  // copy their text into another layer after load (the home and product scroll stories), so copies are picked up too.
  if (marked.length) {
    if (reduce || !('IntersectionObserver' in window)) {
      const all = () => document.querySelectorAll('.rn-mark, .rn-hl').forEach((el) => el.classList.add('is-in'));
      all(); setTimeout(all, 1500); addEventListener('load', all);
    } else {
      const seen = new WeakSet();
      const io = new IntersectionObserver((entries) => {
        entries.forEach((e) => {
          if (!e.isIntersecting) return;
          io.unobserve(e.target);
          e.target.classList.add('is-in');
        });
      }, { rootMargin: '0px 0px -12% 0px' });
      const watch = () => document.querySelectorAll('.rn-mark:not(.is-in), .rn-hl:not(.is-in)').forEach((el) => {
        if (seen.has(el)) return;
        seen.add(el);
        io.observe(el);
      });
      watch();
      new MutationObserver(watch).observe(main, { childList: true, subtree: true });
    }
  }

  /* ---------- 2. No awkward breaks; arrows ---------- */
  const NOWRAP = ['9 am–6 pm ET', '$90 one-time', 'trade-off', 'mid-feed', 'four-bottle'];
  NOWRAP.forEach((phrase) => {
    [...main.querySelectorAll('p, li, dd, span, div, a')]
      .filter((el) => [...el.childNodes].some((n) => n.nodeType === 3 && n.data.includes(phrase)))
      .forEach((el) => wrapWord(el, phrase, () => Object.assign(document.createElement('span'), { className: 'rn-nw' })));
  });
  document.querySelectorAll('main a, main button, footer a').forEach((el) => {
    const last = el.lastChild && el.lastChild.nodeType === 3 ? el.lastChild : null;
    if (!last || !/→\s*$/.test(last.data)) return;
    const idx = last.data.lastIndexOf('→');
    const arrow = last.splitText(idx);
    const span = Object.assign(document.createElement('span'), { className: 'rn-arrow', textContent: '→' });
    arrow.data = arrow.data.replace('→', '');
    el.insertBefore(span, arrow);
    // Keep the arrow on the same line as the word before it.
    const before = span.previousSibling;
    if (before && before.nodeType === 3) before.data = before.data.replace(/\s+$/, '\u00a0');
  });

  /* ---------- 3. Card groups below the fold enter with a stagger ---------- */
  if (!reduce && 'IntersectionObserver' in window && Element.prototype.animate) {
    const ITEM = '.rn-perks__item, .rn-how__card, .feature-grid__item, .rn-f101p-card, .aff-program__stat, .materials-card, .rn-acard, .specs__card, .rn-stat';
    const vh = innerHeight;
    const groups = new Map();
    main.querySelectorAll(ITEM).forEach((el) => {
      if (el.closest('[data-rn], [data-rn-words], [data-no-motion]')) return;
      if (el.getBoundingClientRect().top < vh * 0.95) return;
      const parent = el.parentElement;
      if (!groups.has(parent)) groups.set(parent, []);
      groups.get(parent).push(el);
    });
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (!e.isIntersecting) return;
        io.unobserve(e.target);
        (groups.get(e.target) || []).forEach((el, i) => {
          el.animate({ opacity: [0, 1], translate: ['0 18px', '0 0'] },
            { duration: 520, delay: Math.min(i, 6) * 70, easing: 'cubic-bezier(.2, .7, .2, 1)', fill: 'backwards' })
            .finished.finally(() => el.classList.remove('rn-pi'));
          // The CSS start state is dropped as the animation takes over (fill: backwards holds the first frame).
          requestAnimationFrame(() => el.classList.remove('rn-pi'));
        });
      });
    }, { rootMargin: '0px 0px -8% 0px' });
    groups.forEach((items, parent) => {
      if (items.length < 2) return;
      items.forEach((el) => el.classList.add('rn-pi'));
      io.observe(parent);
    });
  }

  /* ---------- 4. Footer on screen ---------- */
  // Any of the footer group's sections (Instagram, sign-up, links and wordmark): the group is taller than a screen.
  const footers = document.querySelectorAll('.shopify-section-group-footer-group');
  if (footers.length && 'IntersectionObserver' in window) {
    const inView = new Set();
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => (e.isIntersecting ? inView.add(e.target) : inView.delete(e.target)));
      document.documentElement.classList.toggle('rn-at-footer', inView.size > 0);
    }, { rootMargin: '0px 0px -60px 0px' });
    footers.forEach((el) => io.observe(el));
  }

  /* ---------- 6. Drawn illustrations: finished lines, one small loop while on screen ---------- */
  const drawings = document.querySelectorAll('.rn-line');
  // 10-15: the hand-drawn wobble. One shared filter: fractal noise whose frequency drifts slowly, displacing the line
  // strokes by a couple of pixels, so lines move a little the way redrawn frames do (rn-polish.css applies it).
  if (drawings.length && !reduce && !document.getElementById('rn-wobble')) {
    const ns = 'http://www.w3.org/2000/svg';
    const holder = document.createElementNS(ns, 'svg');
    holder.setAttribute('width', '0'); holder.setAttribute('height', '0'); holder.setAttribute('aria-hidden', 'true');
    holder.style.position = 'absolute'; holder.style.width = '0'; holder.style.height = '0'; holder.style.overflow = 'hidden';
    holder.innerHTML = '<filter id="rn-wobble" x="-5%" y="-5%" width="110%" height="110%">' +
      '<feTurbulence type="fractalNoise" baseFrequency="0.02" numOctaves="2" seed="1" result="n"/>' +
      '<feDisplacementMap in="SourceGraphic" in2="n" scale="2.4" xChannelSelector="R" yChannelSelector="G"/></filter>';
    document.body.appendChild(holder);
    // Step the noise like redrawn frames (about 7 a second), only while a drawing is on screen.
    const turb = holder.querySelector('feTurbulence');
    let seed = 1;
    setInterval(() => {
      if (document.hidden || !document.querySelector('.rn-line.is-playing')) return;
      seed = (seed % 4) + 1;
      turb.setAttribute('seed', String(seed));
    }, 150);
  }
  if (drawings.length && 'IntersectionObserver' in window) {
    // 10-15 (owner: the drawings kept flashing): no ink-in. Every drawing shows finished and only its small loop
    // (breathing, a kick, the balloon, steam, drops) plays while on screen. The line 'boil' is off in rn-polish.css.
    const dio = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        const svg = e.target;
        svg.classList.toggle('is-playing', e.isIntersecting);
        if (e.isIntersecting && svg.classList.contains('is-armed')) svg.classList.add('is-drawing');
      });
    }, { rootMargin: '0px 0px -10% 0px' });
    drawings.forEach((el) => dio.observe(el));
  } else {
    drawings.forEach((el) => el.classList.add('is-playing'));
  }

  /* ---------- 7. Big numbers count up once; the award bottle rises into place ---------- */
  if (!reduce && 'IntersectionObserver' in window) {
    const nums = [...main.querySelectorAll('.rn-stat__num')].filter((n) => /^\d+$/.test(n.textContent.trim()) && +n.textContent.trim() > 0);
    if (nums.length) {
      const cio = new IntersectionObserver((entries) => {
        entries.forEach((e) => {
          if (!e.isIntersecting) return;
          cio.unobserve(e.target);
          const el = e.target;
          const end = +el.dataset.rnEnd;
          const dur = Math.min(1400, 800 + end * 12);
          const t0 = performance.now();
          const tick = (t) => {
            const p = Math.min(1, (t - t0) / dur);
            el.textContent = String(Math.round(end * (1 - Math.pow(1 - p, 3))));
            if (p < 1) requestAnimationFrame(tick);
          };
          requestAnimationFrame(tick);
        });
      }, { threshold: 0.6 });
      nums.forEach((el) => {
        const value = el.textContent.trim();
        if (el.getBoundingClientRect().top < innerHeight) return; // already on screen: leave it
        el.style.minWidth = el.getBoundingClientRect().width + 'px';
        el.dataset.rnEnd = value;
        el.classList.add('rn-count');
        el.setAttribute('aria-hidden', 'true');
        el.insertAdjacentHTML('afterend', '<span class="sr-only">' + value + '</span>');
        el.textContent = '0';
        cio.observe(el);
      });
    }
    const rise = [...main.querySelectorAll('.rn-vband__media')].filter((m) => m.querySelector(':scope > img') && m.getBoundingClientRect().top > innerHeight);
    if (rise.length) {
      const rio = new IntersectionObserver((entries) => {
        entries.forEach((e) => {
          if (!e.isIntersecting) return;
          rio.unobserve(e.target);
          e.target.classList.add('rn-rise-in');
        });
      }, { rootMargin: '0px 0px -12% 0px' });
      rise.forEach((m) => { m.classList.add('rn-rise-armed'); rio.observe(m); });
    }
  }
})();
